import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * SuperAdminAuthContext - Completely separate authentication for AI Governance
 * 
 * USER HIERARCHY:
 * - Governor (role="governor") - Lockene internal, CAN access Governor Panel
 * - Superadmin (role="superadmin") - Company owner, CANNOT access Governor Panel
 * - User (role="user") - Company member, CANNOT access Governor Panel
 * 
 * ISOLATION REQUIREMENTS:
 * - Uses separate localStorage key: 'superadmin_token'
 * - JWT includes scope: 'governance' claim
 * - NOT interchangeable with CRM tokens
 * - ONLY accepts role='governor' (Lockene internal team)
 */

const SuperAdminAuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Separate axios instance for governor auth
const governorAuthApi = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const SuperAdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Get token from separate storage
  const getToken = () => localStorage.getItem('superadmin_token');
  const setToken = (token) => localStorage.setItem('superadmin_token', token);
  const removeToken = () => localStorage.removeItem('superadmin_token');

  // Validate existing token on mount
  useEffect(() => {
    const validateToken = async () => {
      const token = getToken();
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await governorAuthApi.get('/api/auth/superadmin/me', {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data) {
          // Verify this is a governance-scoped token
          const userData = response.data;
          if (userData.scope !== 'governance') {
            console.log('[GovernorAuth] Invalid token scope, rejecting');
            removeToken();
            setLoading(false);
            return;
          }

          // Verify role is governor (Lockene internal only)
          if (userData.role !== 'governor') {
            console.log('[GovernorAuth] Invalid role - only governor can access this panel');
            removeToken();
            setLoading(false);
            return;
          }

          setUser(userData);
          setIsAuthenticated(true);
          console.log('[GovernorAuth] Token validated:', userData.email);
        }
      } catch (error) {
        console.log('[GovernorAuth] Token validation failed:', error.response?.status);
        removeToken();
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, []);

  // Login function - uses separate endpoint (Governor only)
  const login = useCallback(async (email, password) => {
    try {
      const response = await governorAuthApi.post('/api/auth/superadmin/login', {
        email,
        password,
      });

      const { access_token, user: userData } = response.data;

      // Verify governance scope in response
      if (userData.scope !== 'governance') {
        throw new Error('Invalid token scope - not a governance token');
      }

      // Verify governor role (Lockene internal only)
      if (userData.role !== 'governor') {
        throw new Error('Access denied - Governor role required. Only Lockene internal team can access this panel.');
      }

      setToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);
      console.log('[GovernorAuth] Login successful:', userData.email);

      return { success: true, user: userData };
    } catch (error) {
      console.error('[GovernorAuth] Login failed:', error);
      const message = error.response?.data?.detail || error.message || 'Login failed';
      return { success: false, error: message };
    }
  }, []);

  // Logout function - clears governor token only
  const logout = useCallback(() => {
    removeToken();
    localStorage.removeItem('superadmin_user');
    setUser(null);
    setIsAuthenticated(false);
    console.log('[GovernorAuth] Logged out');
  }, []);

  // Refresh user data
  const refreshUserData = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await governorAuthApi.get('/api/auth/superadmin/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        setUser(response.data);
      }
    } catch (error) {
      console.error('[GovernorAuth] Refresh failed:', error);
      if (error.response?.status === 401) {
        logout();
      }
    }
  }, [logout]);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    refreshUserData,
    getToken,
  };

  return (
    <SuperAdminAuthContext.Provider value={value}>
      {children}
    </SuperAdminAuthContext.Provider>
  );
};

export const useSuperAdminAuth = () => {
  const context = useContext(SuperAdminAuthContext);
  if (!context) {
    throw new Error('useSuperAdminAuth must be used within a SuperAdminAuthProvider');
  }
  return context;
};

export default SuperAdminAuthContext;
